import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import { Icon } from "../../utils/iconHelper";
import BatchManagementService, {
  BatchDetail,
  BatchLookups,
} from "../../_services/BatchManagementService";
import BatchFormPanel from "./components/BatchFormPanel";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "./batch-management.css";

const formatDate = (d: string | null): string => {
  if (!d) return "—";
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

const Field: React.FC<{ label: string; value: React.ReactNode; muted?: boolean }> = ({ label, value, muted }) => (
  <div>
    <div className="bm-view-field-label">{label}</div>
    <div className={`bm-view-field-value ${muted ? "muted" : ""}`}>{value || "—"}</div>
  </div>
);

/** Card-style section with a coloured icon header (matches the form panel). */
const ViewSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  accent: string;
  children: React.ReactNode;
}> = ({ icon, title, accent, children }) => (
  <div className="bm-view-section">
    <div className="bm-view-section-head">
      <span className="bm-view-section-icon" style={{ background: `${accent}1a`, color: accent }}>
        {icon}
      </span>
      <span className="bm-view-section-title">{title}</span>
    </div>
    {children}
  </div>
);

const BatchViewPage: React.FC = () => {
  useDocumentTitle("Batch Detail");
  const history = useHistory();
  const { batchId } = useParams<{ batchId: string }>();
  const id = Number(batchId);

  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [lookups, setLookups] = useState<BatchLookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    loadBatch();
    loadLookups();
  }, [id]);

  const loadBatch = async () => {
    setLoading(true);
    const r = await BatchManagementService.getBatchById(id);
    if (r.success && r.data) setBatch(r.data);
    else setError(r.message || "Failed to load batch");
    setLoading(false);
  };

  const loadLookups = async () => {
    const r = await BatchManagementService.getLookups();
    if (r.success) setLookups(r.data);
  };

  if (loading) {
    return (
      <div className="bm-page">
        <div className="bm-loading">
          <Spinner animation="border" variant="primary" role="status" />
          <p>Loading batch details...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="bm-page">
        <button className="bm-back-link" onClick={() => history.push("/batch-management")}>
          <Icon.ArrowLeft size={14} /> Back to batches
        </button>
        <Alert variant="danger">{error || "Batch not found"}</Alert>
      </div>
    );
  }

  const isProgramBased = lookups?.isProgramBased ?? false;

  return (
    <div className="bm-page">
      <button className="bm-back-link" onClick={() => history.push("/batch-management")}>
        <Icon.ArrowLeft size={14} /> Back to batches
      </button>

      <div className="bm-header">
        <div className="bm-view-head">
          <span className="bm-view-badge">
            <Icon.Layers size={22} />
          </span>
          <div>
            <h1>{batch.name || "(unnamed)"}</h1>
            <div className="bm-view-sub">
              {batch.batchCode && (
                <span>Code: <strong>{batch.batchCode}</strong></span>
              )}
              {batch.batchShortCode && (
                <span>Short Code: <strong>{batch.batchShortCode}</strong></span>
              )}
              <span>
                Status:{" "}
                {batch.activeStatus === 1
                  ? <span className="bm-status-pill bm-status-active">Active</span>
                  : <span className="bm-status-pill bm-status-inactive">Inactive</span>}
              </span>
            </div>
          </div>
        </div>
        <button className="bm-btn-create" onClick={() => setShowEdit(true)}>
          <Icon.Edit2 size={16} />
          Edit Batch
        </button>
      </div>

      <ViewSection icon={<Icon.Info size={16} />} title="Basic Information" accent="#6366f1">
        <div className="bm-view-grid">
          <Field label="Name" value={batch.name} />
          <Field label="Display Name" value={batch.displayName} />
          <Field label="Batch Code" value={batch.batchCode} />
          <Field label="Short Code" value={batch.batchShortCode} />
        </div>
      </ViewSection>

      <ViewSection icon={<Icon.Layers size={16} />} title="Assignments" accent="#8b5cf6">
        <div className="bm-view-grid">
          <Field label="BU (School)" value={batch.schoolName} />
          {isProgramBased ? (
            <>
              <Field label="Program" value={batch.programName} />
              <Field label="Current Term" value={batch.currentTermName} />
            </>
          ) : (
            <Field label="Course" value={batch.courseName} />
          )}
          <Field label="Intake (Year)" value={batch.yearName} />
          <Field label="Type" value={batch.batchTypeName} />
          <Field
            label="Parent Batch"
            value={batch.parentName ? `${batch.parentName}${batch.parentBatchCode ? ` (${batch.parentBatchCode})` : ""}` : null}
          />
        </div>
      </ViewSection>

      <ViewSection icon={<Icon.Calendar size={16} />} title="Schedule & Capacity" accent="#10b981">
        <div className="bm-view-grid">
          <Field label="Start Date" value={formatDate(batch.startDate)} />
          <Field label="End Date" value={formatDate(batch.endDate)} />
          <Field label="Start Time" value={batch.startTime} />
          <Field label="End Time" value={batch.endTime} />
          <Field label="Max Strength" value={batch.maxStrength != null ? String(batch.maxStrength) : null} />
          <Field label="Sort Order" value={batch.sortOrder != null ? String(batch.sortOrder) : null} />
        </div>
      </ViewSection>

      {batch.tags && batch.tags.length > 0 && (
        <ViewSection icon={<Icon.Star size={16} />} title="Tags" accent="#f59e0b">
          <div className="bm-view-chips">
            {batch.tags.map((t) => (
              <span key={t.id} className="bm-view-chip" title={t.typeName || ""}>
                {t.name}
              </span>
            ))}
          </div>
        </ViewSection>
      )}

      {(batch.locationText || batch.remarks) && (
        <ViewSection icon={<Icon.MapPin size={16} />} title="Additional Info" accent="#64748b">
          <div className="bm-view-grid">
            {batch.locationText && <Field label="Location" value={batch.locationText} />}
            {batch.remarks && (
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Remarks" value={batch.remarks} />
              </div>
            )}
          </div>
        </ViewSection>
      )}

      {lookups && (
        <BatchFormPanel
          show={showEdit}
          onHide={() => setShowEdit(false)}
          batchId={id}
          lookups={lookups}
          onSuccess={() => {
            setShowEdit(false);
            loadBatch();
          }}
        />
      )}
    </div>
  );
};

export default BatchViewPage;
